import * as React from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, ChevronRight, User2, UserPlus, Calendar as CalendarIcon,
  Coins, ChevronDown,
} from "lucide-react";
import { fmtCents } from "@/lib/time";
import { useCompany } from "@/context/CompanyContext";
import { EmptyState } from "@/components/feel/EmptyState";

/**
 * OrgView — positions grouped by site with reporting-line tree.
 *
 * Renders the ``/hr/org`` payload as:
 *   - Headcount strip (filled / open / total across the whole tenant).
 *   - Per-site sections with an indented tree of positions.  Each node
 *     shows the position title, holder name (or "OPEN" pill), and — if
 *     the assignment carries a ``rate_override_cents`` — a small
 *     violet OVR chip.
 *
 * "As of" date picker at the top; changing it re-fetches so operators
 * can inspect history.
 */
export default function OrgView() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("hr.manage");
  const [asOf, setAsOf] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const { data: d } = await api.get(`/companies/${companyId}/hr/org`,
                                          { params: { as_of: asOf } });
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load org");
      setData({ sites: [], totals: { filled: 0, open: 0, total: 0 } });
    }
  }, [companyId, asOf]);

  React.useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="p-6 text-[12px] text-brand-muted">Loading…</div>;
  }

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1400px]" data-testid="org-view-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center">
          <Building2 size={17} />
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Organisation</div>
          <div className="text-[11.5px] text-brand-muted">
            Positions grouped by site with reporting line. Open positions in coral. Rate override chip flags per-assignment overrides.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <CalendarIcon size={13} className="text-brand-muted" />
          <input type="date" value={asOf}
                 onChange={(e) => setAsOf(e.target.value)}
                 className="field-input !h-8 !w-[130px] text-[12px] mono-num"
                 data-testid="org-asof-picker"/>
        </div>
      </div>

      {/* Headcount strip */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="org-headcount-strip">
        <Stat label="Filled" value={data.totals.filled} tone="emerald" />
        <Stat label="Open"   value={data.totals.open}   tone="coral" />
        <Stat label="Total"  value={data.totals.total}  tone="violet" />
      </div>

      {/* Sites */}
      {data.sites.length === 0 ? (
        <EmptyState icon={Building2} eyebrow="Organisation" title="No positions defined yet." />
      ) : (
        <div className="space-y-6">
          {data.sites.map((s) => <SiteBlock key={s.site.id} block={s} />)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    coral:   "bg-coral/10 border-coral/30 text-coral",
    violet:  "bg-accent border-brand-rule text-brand-violet",
  }[tone];
  return (
    <div className={`rounded-md border ${bg} p-3`} data-testid={`org-stat-${label.toLowerCase()}`}>
      <div className="micro-label">{label}</div>
      <div className="mono-num text-[24px] font-bold leading-tight">{value}</div>
    </div>
  );
}

function SiteBlock({ block }) {
  const site = block.site;
  const positions = block.positions;
  // Build parent → children map for tree render
  const byId = new Map(positions.map((n) => [n.position.id, n]));
  const roots = [];
  const childrenBy = new Map();
  for (const node of positions) {
    const parent = node.reports_to_position_id;
    if (parent && byId.has(parent)) {
      const arr = childrenBy.get(parent) || [];
      arr.push(node);
      childrenBy.set(parent, arr);
    } else {
      roots.push(node);
    }
  }
  // Flatten to (node, depth) rows iteratively — avoids babel's recursive
  // JSX blow-up.
  const flat = [];
  const stack = roots.map((r) => ({ node: r, depth: 0 }));
  while (stack.length) {
    const cur = stack.pop();
    flat.push(cur);
    const kids = childrenBy.get(cur.node.position.id) || [];
    for (let i = kids.length - 1; i >= 0; i--) {
      stack.push({ node: kids[i], depth: cur.depth + 1 });
    }
  }

  return (
    <section data-testid={`org-site-${site.id}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="micro-label">SITE</div>
          <div className="text-[15px] font-semibold text-brand-ink">{site.name}</div>
        </div>
        <div className="mono-num text-[11px] text-brand-muted">
          {block.filled} filled · {block.open} open · {block.total} total
        </div>
      </div>
      <div className="card-panel p-2">
        {flat.map(({ node, depth }) => (
          <TreeRow key={node.position.id} node={node} depth={depth} />
        ))}
      </div>
    </section>
  );
}

function TreeRow({ node, depth }) {
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 hover:bg-brand-canvas-2 rounded"
      style={{ paddingLeft: 8 + depth * 20 }}
      data-testid={`org-position-${node.position.id}`}
    >
      {depth > 0 && <ChevronRight size={10} className="text-brand-faint shrink-0" />}
      <User2 size={11} className={node.holder ? "text-brand-violet" : "text-coral"} />
      <span className="text-[12.5px] font-medium text-brand-ink truncate">
        {node.position.title}
      </span>
      {node.position.department && (
        <span className="chip text-[9.5px] mono-num text-brand-muted">
          {node.position.department}
        </span>
      )}
      <span className="ml-auto text-[11.5px] mono-num truncate max-w-[220px]">
        {node.holder ? (
          <span className="text-brand-ink flex items-center gap-1 justify-end">
            <span>{node.holder.name}</span>
            {node.holder.rate_override_cents != null && (
              <span className="chip chip-violet text-[9.5px] mono-num inline-flex items-center gap-0.5"
                    title={`Override: ${fmtCents(node.holder.rate_override_cents)}`}>
                <Coins size={9}/>{fmtCents(node.holder.rate_override_cents)}
              </span>
            )}
          </span>
        ) : (
          <span className="chip chip-coral text-[9.5px] mono-num">OPEN</span>
        )}
      </span>
    </div>
  );
}
